/**
 * The model's thinking, in the shape `bimgo-ai-web`'s `ReasoningSection` gives
 * it.
 *
 * The first pass here was a "Show reasoning" toggle over a `<pre>`. Two things
 * were wrong with that, and both matter more while a turn is running than after:
 *
 *   - **While it streams, the reasoning is the only thing happening.** Folding
 *     it behind a toggle left a blank transcript during the longest part of a
 *     turn. The reference shows the *latest line* beside a spinner instead, so
 *     the wait has something in it and the user can see the model is working the
 *     problem rather than stuck.
 *   - **Once it is done, one line is enough.** The reference shows the **first**
 *     line — reasoning opens with what it is about to do — and hides the rest
 *     behind a chevron. A `<pre>` of the whole thing buried the answer below it
 *     and rendered markdown as literal asterisks.
 *
 * Rendered through the app's `Markdown`, as the reference renders it through
 * `ReactMarkdown`: reasoning is written in the same dialect as the answer, and
 * shown raw it reads as `**Exploring tool limitations**`.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Markdown } from "./Markdown";
import { Spinner } from "../settings/Spinner";
import { foldReasoning, latestReasoningLine } from "../../lib/reasoning";

/** lucide `Lightbulb`. */
function LightbulbIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {up ? <path d="m18 15-6-6-6 6" /> : <path d="m6 9 6 6 6-6" />}
    </svg>
  );
}

export function ReasoningSection({ text, complete }: { text: string; complete: boolean }) {
  const { t } = useLocale();
  const [collapsed, setCollapsed] = useState(true);

  if (!complete) {
    // The newest line, not the first: while it streams, what the model is
    // thinking *now* is the only part worth a line of the screen.
    const latest = latestReasoningLine(text);
    return (
      <div className="reasoning is-live">
        <span className="reasoning-icon" aria-hidden="true">
          <Spinner size={16} />
        </span>
        <div className="reasoning-line">{latest ?? t("common.thinking")}</div>
      </div>
    );
  }

  const { first, rest } = foldReasoning(text);
  // Reasoning that arrived as nothing but blank lines is not worth a row.
  if (!first) return null;

  return (
    <div className="reasoning">
      <div className="reasoning-head">
        <span className="reasoning-icon" aria-hidden="true">
          <LightbulbIcon />
        </span>
        <div className="reasoning-first">
          <Markdown text={first} />
        </div>
        {rest.length > 0 ? (
          <button
            type="button"
            className="reasoning-toggle"
            aria-expanded={!collapsed}
            aria-label={collapsed ? t("chat.showReasoning") : t("chat.hideReasoning")}
            title={collapsed ? t("chat.showReasoning") : t("chat.hideReasoning")}
            onClick={() => setCollapsed((v) => !v)}
          >
            <ChevronIcon up={!collapsed} />
          </button>
        ) : null}
      </div>
      {!collapsed && rest.length > 0 ? (
        <div className="reasoning-rest">
          <Markdown text={rest} />
        </div>
      ) : null}
    </div>
  );
}
